import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, ShieldCheck, AlertTriangle, CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function AdminPaymentTest() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [diag, setDiag] = useState(null);
  const [starting, setStarting] = useState(false);
  const [amount, setAmount] = useState(1);
  const [probe, setProbe] = useState(null);
  const [charges, setCharges] = useState([]);
  const probeSession = searchParams.get("probe_session");
  const canceled = searchParams.get("canceled");

  const loadDiag = useCallback(() => {
    api.get("/admin/payments/diagnostics").then(({ data }) => setDiag(data)).catch(() => setDiag({ mode: "error" }));
  }, []);
  const loadCharges = useCallback(() => {
    api.get("/admin/payments/test-charges").then(({ data }) => setCharges(data.items || [])).catch(() => {});
  }, []);
  useEffect(() => { loadDiag(); loadCharges(); }, [loadDiag, loadCharges]);

  // Poll probe status after returning from Stripe
  useEffect(() => {
    if (!probeSession) return;
    let tries = 0;
    setProbe({ state: "checking" });
    const poll = async () => {
      try {
        const { data } = await api.get(`/admin/payments/test-status/${probeSession}`);
        if (data.payment_status === "paid") {
          setProbe({ state: "paid", ...data });
          loadCharges();
        } else if (tries++ < 8) {
          setTimeout(poll, 1500);
        } else {
          setProbe({ state: "pending", ...data });
        }
      } catch {
        setProbe({ state: "error" });
      }
    };
    poll();
  }, [probeSession]);

  const startTest = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/admin/payments/test-checkout", { origin_url: window.location.origin, amount: Number(amount) });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start test checkout");
      setStarting(false);
    }
  };

  const modeBadge = () => {
    if (!diag) return <span className="inline-flex items-center gap-1 text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> checking…</span>;
    if (diag.mode === "live") return <span data-testid="mode-badge" className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-700 px-3 py-1 rounded-sm text-sm font-bold uppercase tracking-wider"><ShieldCheck className="w-4 h-4" /> LIVE MODE — real payments</span>;
    if (diag.mode === "test") return <span data-testid="mode-badge" className="inline-flex items-center gap-1.5 bg-amber-100 text-amber-700 px-3 py-1 rounded-sm text-sm font-bold uppercase tracking-wider"><AlertTriangle className="w-4 h-4" /> TEST MODE — no real money</span>;
    return <span data-testid="mode-badge" className="inline-flex items-center gap-1.5 bg-red-100 text-red-700 px-3 py-1 rounded-sm text-sm font-bold uppercase tracking-wider"><XCircle className="w-4 h-4" /> UNKNOWN / MISCONFIGURED</span>;
  };

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8 max-w-3xl" data-testid="admin-payment-test">
        <div className="flex items-center gap-2 label-overline text-[#E63946]"><CreditCard className="w-4 h-4" /> Payments · Diagnostics</div>
        <h1 className="font-display text-3xl uppercase tracking-tight">Stripe Payment Test</h1>
        <p className="text-sm text-slate-500 mt-1">Run this <strong>on the environment you want to verify</strong>. On cbrprospects.com it reflects your production Stripe key.</p>

        {/* Diagnostics card */}
        <div className="mt-6 bg-white border border-slate-200 rounded-sm p-5" data-testid="diagnostics-card">
          <div className="flex items-center justify-between">
            <h2 className="font-display uppercase tracking-tight text-lg">Environment</h2>
            {modeBadge()}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div className="border border-slate-100 rounded-sm p-3"><div className="label-overline text-[10px]">Key prefix</div><div className="font-data mt-1">{diag?.key_prefix || "—"}…</div></div>
            <div className="border border-slate-100 rounded-sm p-3"><div className="label-overline text-[10px]">Webhook secret</div><div className="mt-1">{diag?.webhook_secret_set ? <span className="text-emerald-600 font-semibold">Configured</span> : <span className="text-amber-600 font-semibold">Not set (poll fallback active)</span>}</div></div>
          </div>
          {diag && diag.mode === "test" && (
            <div className="mt-4 bg-amber-50 border border-amber-200 text-amber-800 rounded-sm p-3 text-xs">
              This environment is in <strong>TEST mode</strong>. Use Stripe test cards (e.g. <span className="font-data">4242 4242 4242 4242</span>, any future expiry/CVC). <strong>No real money moves.</strong> To take real payments, set <span className="font-data">STRIPE_API_KEY</span> to your <span className="font-data">sk_live_…</span> key on production and redeploy.
            </div>
          )}
          {diag && diag.mode === "live" && (
            <div className="mt-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-sm p-3 text-xs">
              This environment is in <strong>LIVE mode</strong> — a test charge below uses a <strong>real card</strong> and moves <strong>real money</strong>. Refund it afterwards from your Stripe dashboard.
            </div>
          )}
        </div>

        {/* Result banner */}
        {canceled && (
          <div className="mt-4 bg-slate-100 border border-slate-200 rounded-sm p-4 text-sm text-slate-600" data-testid="probe-canceled">Test checkout was canceled. No charge was made.</div>
        )}
        {probe && (
          <div className="mt-4 rounded-sm p-4 border" data-testid="probe-result">
            {probe.state === "checking" && <div className="flex items-center gap-2 text-slate-600"><Loader2 className="w-4 h-4 animate-spin" /> Confirming payment with Stripe…</div>}
            {probe.state === "paid" && (
              <div className="bg-emerald-50 border-emerald-200 -m-4 p-4 rounded-sm">
                <div className="flex items-center gap-2 text-emerald-700 font-bold"><CheckCircle2 className="w-5 h-5" /> PAYMENT SUCCESSFUL — pipeline is functional</div>
                <p className="text-sm text-emerald-800 mt-2">Charged {(probe.amount_total ? (probe.amount_total / 100).toFixed(2) : "")} {(probe.currency || "cad").toUpperCase()} in <strong>{probe.mode?.toUpperCase()}</strong> mode. {probe.mode === "live" ? "This was REAL money — refund it from your Stripe dashboard (Payments → this charge → Refund)." : "This was a TEST charge — no real money."}</p>
                <p className="text-xs text-emerald-700 mt-1">Confirm it appears in your Stripe Dashboard under <strong>{probe.mode === "live" ? "Live" : "Test"} → Payments</strong>.</p>
              </div>
            )}
            {probe.state === "pending" && <div className="flex items-center gap-2 text-amber-700"><AlertTriangle className="w-4 h-4" /> Payment status: {probe.payment_status || "pending"}. Check your Stripe dashboard.</div>}
            {probe.state === "error" && <div className="flex items-center gap-2 text-red-600"><XCircle className="w-4 h-4" /> Could not confirm status. Check Stripe dashboard.</div>}
          </div>
        )}

        {/* Run test */}
        <div className="mt-4 bg-white border border-slate-200 rounded-sm p-5">
          <h2 className="font-display uppercase tracking-tight text-lg">Run a test charge</h2>
          <p className="text-sm text-slate-500 mt-1">Creates a one-off Stripe Checkout (no registration, no emails). {diag?.mode === "live" ? "Real card required." : "Use test card 4242 4242 4242 4242."}</p>
          <div className="mt-3 flex items-center gap-2">
            <div className="flex items-center border border-slate-300 rounded-sm px-2">
              <span className="text-slate-400 text-sm">$</span>
              <input data-testid="test-amount" type="number" min="0.5" step="0.5" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-20 px-1 py-2 text-sm outline-none" />
              <span className="text-slate-400 text-xs pr-1">CAD</span>
            </div>
            <button data-testid="start-test-payment" onClick={startTest} disabled={starting} className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-5 py-2.5 text-xs font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60">
              {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Start ${Number(amount || 1).toFixed(2)} Test Payment
            </button>
          </div>
        </div>

        {/* Recent test charges */}
        <div className="mt-4 bg-white border border-slate-200 rounded-sm p-5" data-testid="test-charges-card">
          <h2 className="font-display uppercase tracking-tight text-lg">Recent Test Charges</h2>
          <p className="text-xs text-slate-500 mt-1">Standalone test charges. These do <strong>not</strong> appear on the main Payments page (that page tracks real camp registrations). Refund any live ones from your Stripe dashboard.</p>
          {charges.length === 0 ? (
            <p className="text-sm text-slate-400 mt-3">No test charges yet.</p>
          ) : (
            <table className="w-full text-sm mt-3">
              <thead className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
                <tr><th className="py-2">When</th><th className="py-2">Amount</th><th className="py-2">Mode</th><th className="py-2">Status</th></tr>
              </thead>
              <tbody>
                {charges.map((c) => (
                  <tr key={c.id} data-testid={`test-charge-${c.id}`} className="border-b border-slate-50">
                    <td className="py-2 text-slate-500 text-xs">{(c.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="py-2 font-data">${Number(c.amount || 0).toFixed(2)} {c.currency}</td>
                    <td className="py-2"><span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm ${c.mode === "live" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{c.mode || "—"}</span></td>
                    <td className="py-2"><span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm ${c.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{c.status === "paid" ? "paid" : (c.payment_status || c.status)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
